import AvatarGroup from "components/common/Avatar/AvatarGroup";
import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import CustomButton from "components/common/Button/CustomButton";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import Header from "components/common/Header/Header";
import PaginationComponent from "components/common/Pagination/index";
import Search from "components/common/Search/Search";
import SelectOption from "components/common/Select/Select";
import SideBarNew from "components/common/SideBar/SideBarNew";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";
import TableHeader from "components/common/Table/TableHeader";
import { emptyPaginationObj, SORT_TYPE, USER_ROLE, getParticipantLabel } from "Constants/constant";
import { addMonths } from "date-fns";
import { LoginContext } from "helpers/LoginContext";
import { roleAccess } from "helpers/RolesPermission";
import moment from "moment";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    AiOutlineCheckCircle,
    AiOutlineEdit,
    AiOutlineFilePdf,
    AiOutlinePlus,
    AiOutlineWarning,
} from "react-icons/ai";
import { BiFilterAlt, BiPencil, BiTime } from "react-icons/bi";
import { BsArrowRight } from "react-icons/bs";
import {
    FiActivity,
    FiAlertTriangle,
    FiCheckCircle,
    FiClock,
    FiFileText,
    FiShield,
} from "react-icons/fi";
import {
    HiEye,
    HiOutlineClipboardList,
    HiOutlineTrendingDown,
    HiOutlineTrendingUp,
    HiTrash,
} from "react-icons/hi";
import { MdOutlineNotStarted } from "react-icons/md";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getSPWorkerRequest } from "store/actions/sp.actions";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import { incidentActions } from "store/sagas/incident.sagas";
import api from "store/services/incident.services";
import CheckBox from "components/common/checkBox/checkbox";
import {
    getAssignedParticipants,
    getIncidentDashboard,
    getIncidentDashboardLoading,
    getIncidentDeleteLoading,
    getIncidentDeleteSuccess,
    getIncidentList,
    getIncidentListLoading,
    getBulkPdfGenerateLoading,
    getBulkPdfGenerateSuccess,
} from "store/selector/incident.selector";
import { getSpWorkers, getSpSetting } from "store/selector/sp.selector";
import { capitalizeWords, classNames } from "Utils/utils";
import {
    INCIDENT_LIST_LIMIT,
    INCIDENT_STATUS_LABELS,
    INCIDENT_STATUS_OPTIONS,
    OUTCOME_OPTIONS,
} from "./constants";

const BREADCRUMB = [
  { name: "Incident Management", href: "/incident-management" },
];

// Enhanced Dashboard Card Component - Clean Top Border Accent
const DashboardCard = ({
  title,
  count,
  icon: Icon,
  color,
  accentColor,
  trend,
  trendValue,
  onClick,
  isActive = false,
}) => (
  <div
    onClick={onClick}
    className={classNames(
      "relative bg-white rounded-xl shadow-sm cursor-pointer transition-all duration-300 group overflow-hidden",
      "hover:shadow-lg hover:-translate-y-1",
      isActive ? "border-t-4" : "border border-gray-100 hover:border-gray-200",
    )}
    style={isActive ? { borderTopColor: accentColor } : {}}
  >
    <div className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className={classNames("text-3xl font-bold", color)}>
            {count || 0}
          </p>

          {/* Trend Indicator */}
          {trend && (
            <div
              className={classNames(
                "flex items-center gap-1 mt-2 text-xs font-medium",
                trend === "up"
                  ? "text-green-600"
                  : trend === "down"
                    ? "text-red-600"
                    : "text-gray-500",
              )}
            >
              {trend === "up" ? (
                <HiOutlineTrendingUp className="w-4 h-4" />
              ) : trend === "down" ? (
                <HiOutlineTrendingDown className="w-4 h-4" />
              ) : null}
              <span>{trendValue}</span>
            </div>
          )}
        </div>

        {/* Icon Container */}
        <div className="p-3 rounded-xl bg-gray-50 transition-transform group-hover:scale-110">
          {Icon && <Icon className={classNames("w-6 h-6", color)} />}
        </div>
      </div>
    </div>

    {/* Hover Arrow */}
    <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
      <BsArrowRight className="w-4 h-4 text-gray-400" />
    </div>
  </div>
);

const IncidentDashboard = (props) => {
  const {
    getIncidentListCall,
    incidentList,
    incidentListLoading,
    getIncidentDashboardCall,
    incidentDashboard,
    incidentDashboardLoading,
    getWorkerListCall,
    workersList,
    deleteIncidentCall,
    deleteLoading,
    deleteSuccess,
    resetDeleteCall,
    getAssignedParticipantsCall,
    assignedParticipants,
    settings,
    bulkGeneratePdfCall,
    bulkPdfLoading,
    bulkPdfSuccess,
    resetBulkPdfCall,
  } = props;

  const navigate = useNavigate();
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const participantLabel = getParticipantLabel(settings?.care_sector, false);

  // State
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDescending, setSortDescending] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [pdfLoadingUuid, setPdfLoadingUuid] = useState(null);
  const [checkboxes, setCheckboxes] = useState([]);
  const [alreadyGeneratedCount, setAlreadyGeneratedCount] = useState(0);

  // Filters
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null); // For date range restriction

  // Options
  const [participantOptions, setParticipantOptions] = useState([]);
  const [workerOptions, setWorkerOptions] = useState([]);

  // Load initial data
  useEffect(() => {
    getIncidentDashboardCall({});
    // Use role-based participant fetch (saga handles role detection internally)
    getAssignedParticipantsCall();
    getWorkerListCall();
  }, []);

  // Fetch incidents
  const fetchIncidents = useCallback(
    (filters = {}) => {
      // Allow direct status override for immediate filtering (e.g., from banner clicks)
      const statusValue =
        filters.status !== undefined
          ? filters.status
          : selectedStatus?.value || null;

      // Allow direct date overrides for immediate filtering (e.g., from "This Week" card)
      const dateFrom =
        filters.date_from !== undefined
          ? filters.date_from
          : fromDate
            ? moment(fromDate).format("YYYY-MM-DD")
            : null;

      const dateTo =
        filters.date_to !== undefined
          ? filters.date_to
          : toDate
            ? moment(toDate).format("YYYY-MM-DD")
            : null;

      const params = {
        filters: {
          page: filters.page || page,
          per_page: INCIDENT_LIST_LIMIT,
          search: filters.search !== undefined ? filters.search : search,
          sort_field: filters.sort_field || sortBy,
          sort_order:
            filters.sort_order ||
            (sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC),
        },
        bodyFilter: {
          status: statusValue,
          outcome_type: selectedOutcome?.value || null,
          participant_id: selectedParticipant?.value,
          worker_id: selectedWorker?.value,
          date_from: dateFrom,
          date_to: dateTo,
        },
      };
      getIncidentListCall(params);
    },
    [
      page,
      search,
      sortBy,
      sortDescending,
      selectedStatus,
      selectedOutcome,
      selectedParticipant,
      selectedWorker,
      fromDate,
      toDate,
    ],
  );

  // Fetch incidents when page or filters change
  useEffect(() => {
    // Build params directly to avoid stale closure issues
    const statusValue = selectedStatus?.value || null;
    const dateFrom = fromDate ? moment(fromDate).format("YYYY-MM-DD") : null;
    const dateTo = toDate ? moment(toDate).format("YYYY-MM-DD") : null;

    const params = {
      filters: {
        page: page, // Use current page state directly
        per_page: INCIDENT_LIST_LIMIT,
        search: search,
        sort_field: sortBy,
        sort_order: sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
      },
      bodyFilter: {
        status: statusValue,
        outcome_type: selectedOutcome?.value || null,
        participant_id: selectedParticipant?.value,
        worker_id: selectedWorker?.value,
        date_from: dateFrom,
        date_to: dateTo,
      },
    };
    getIncidentListCall(params);
  }, [
    page, // Primary trigger - only fetch when page number changes
    // Include filter dependencies to ensure correct data is fetched for the new page
    search,
    sortBy,
    sortDescending,
    selectedStatus,
    selectedOutcome,
    selectedParticipant,
    selectedWorker,
    fromDate,
    toDate,
    getIncidentListCall,
  ]);

  // Update pagination state from API response
  useEffect(() => {
    if (incidentList?.incidents) {
      // Handle both Laravel pagination formats (last_page) and custom formats (total_pages)
      const apiPage = incidentList.incidents.current_page || incidentList.incidents.page || 1;
      const apiTotalPages =
        incidentList.incidents.last_page ||
        incidentList.incidents.total_pages ||
        1;
      const apiTotal = incidentList.incidents.total || 0;

      // Don't auto-sync page state - let user's page selection take precedence
      // The paginationObj will use API page for display, but page state controls what we request

      // Always update pagination object when incidentList changes
      // This ensures pagination is always in sync with API response
      setPaginationObj({
        page: apiPage, // Use API page for display
        totalPages: apiTotalPages,
        total: apiTotal,
      });
    }
  }, [incidentList]); // Removed 'page' from dependencies to prevent reset loops

  // Format participant options (saga normalizes data for both admin and worker)
  useEffect(() => {
    if (assignedParticipants?.data) {
      const participantsData = assignedParticipants.data;
      if (Array.isArray(participantsData)) {
        const options = participantsData.map((p) => ({
          value: p?.id,
          label: `${p?.first_name || ""} ${p?.last_name || ""}`.trim(),
        }));
        setParticipantOptions(options);
      }
    }
  }, [assignedParticipants]);

  useEffect(() => {
    if (workersList) {
      const options = workersList.map((w) => ({
        value: w.uuid || String(w.id || w.value), // Use UUID for dropdown matching (same as CreateIncidentContainer)
        label:
          w.label ||
          w.name ||
          `${w.first_name || ""} ${w.last_name || ""}`.trim(),
      }));
      setWorkerOptions(options);
    }
  }, [workersList]);

  // Handlers
  const handleSearch = (searchValue) => {
    setPage(1);
    fetchIncidents({ page: 1, search: searchValue });
  };

  // Track if component has mounted to avoid re-fetch on initial empty search
  const isSearchMountedRef = useRef(false);

  useEffect(() => {
    // Skip initial mount
    if (!isSearchMountedRef.current) {
      isSearchMountedRef.current = true;
      return;
    }

    // Debounce search - works for both typing and clearing
    const timeoutId = setTimeout(() => {
      handleSearch(search);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const handleSort = (field) => {
    const newSortDesc = sortBy === field ? !sortDescending : true;
    setSortBy(field);
    setSortDescending(newSortDesc);
    fetchIncidents({
      sort_field: field,
      sort_order: newSortDesc ? SORT_TYPE.DESC : SORT_TYPE.ASC,
    });
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchIncidents({ page: 1 });
  };

  const handleClearFilters = () => {
    setSelectedStatus(null);
    setSelectedOutcome(null);
    setSelectedParticipant(null);
    setSelectedWorker(null);
    setFromDate(null);
    setToDate(null);
    setSearch("");
    setPage(1);
    getIncidentListCall({
      filters: { page: 1, per_page: INCIDENT_LIST_LIMIT },
      bodyFilter: null,
    });
  };

  const handleDashboardCardClick = (status) => {
    setSelectedStatus({ value: status, label: INCIDENT_STATUS_LABELS[status] });
    setPage(1);
    // Pass status directly to avoid async state update issue
    fetchIncidents({ page: 1, status: status });
  };

  // Check if user is manager/admin (for showing review banner)
  const isManagerOrAdmin = roleAccess([
    USER_ROLE.Sp_Admin,
    USER_ROLE.Sp_manager,
  ]);

  // Handle delete
  const handleDeleteClick = (incident) => {
    setSelectedIncident(incident);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = (confirmed) => {
    if (confirmed && selectedIncident) {
      deleteIncidentCall({ uuid: selectedIncident.uuid });
    }
    setShowDeleteModal(false);
    setSelectedIncident(null);
  };

  const incidents = incidentList?.incidents?.data || [];

  // API may return boolean, int, or string "1"/"true"
  const isPdfGenerated = (incident) => {
    const generated = incident?.pdf_generated;
    const hasGeneratedFlag =
      generated === true ||
      generated === 1 ||
      generated === "1" ||
      generated === "true";
    return hasGeneratedFlag || !!incident?.pdf_generated_at;
  };

  // Handle PDF download
  const handlePdfDownload = (incident) => {
    if (!isPdfGenerated(incident)) return;
    setPdfLoadingUuid(incident.uuid);
    api
      .getIncidentPdfBlob({ uuid: incident.uuid })
      .then((res) => {
        const contentType = res.headers["content-type"] || "";
        if (!contentType.includes("application/pdf")) {
          return;
        }
        const blob = res.data;
        const disposition = res.headers["content-disposition"];
        let filename = "incident-report.pdf";
        if (disposition && disposition.includes("filename=")) {
          const m = disposition.match(/filename="?([^"]+)"?/);
          if (m) filename = m[1].trim();
        }
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
      })
      .catch(() => {
        toast.error("Could not download the report. Try again or contact support.");
      })
      .finally(() => {
        setPdfLoadingUuid(null);
      });
  };

  // Checkbox handlers
  const handleCheckboxChange = (e, uuid) => {
    const filteredList = checkboxes.filter((c) => c !== uuid);
    if (e) {
      filteredList.push(uuid);
    }
    setCheckboxes(filteredList);
  };

  // Only closed/reported incidents are eligible for PDF generation
  // Select All: add current page eligible to selection. Deselect All: remove only current page from selection.
  // Never resets selections from other pages.
  const toggleCheckboxes = (val) => {
    const eligibleIncidents = incidents.filter(
      (item) => item.status === "closed" || item.status === "reported"
    );
    const currentPageUuids = eligibleIncidents.map((el) => el.uuid);
    if (val) {
      setCheckboxes((prev) => {
        const merged = new Set([...prev, ...currentPageUuids]);
        return [...merged];
      });
    } else {
      setCheckboxes((prev) => prev.filter((uuid) => !currentPageUuids.includes(uuid)));
    }
  };

  // Header checkbox: checked only when ALL eligible rows on CURRENT page are selected
  const eligibleOnCurrentPage = incidents.filter(
    (item) => item.status === "closed" || item.status === "reported"
  );
  const allSelected =
    eligibleOnCurrentPage.length > 0 &&
    eligibleOnCurrentPage.every((inc) => checkboxes.includes(inc.uuid));

  // Handle bulk PDF generation
  const handleBulkGeneratePdf = () => {
    if (checkboxes.length === 0) return;
    setAlreadyGeneratedCount(0);
    bulkGeneratePdfCall({ uuids: checkboxes });
  };

  useEffect(() => {
    if (bulkPdfSuccess) {
      const { generated = [], already_existed = [] } = bulkPdfSuccess;

      setAlreadyGeneratedCount(already_existed.length);

      // Refresh the list to update PDF icon states immediately
      const statusValue = selectedStatus?.value || null;
      const dateFrom = fromDate ? moment(fromDate).format("YYYY-MM-DD") : null;
      const dateTo = toDate ? moment(toDate).format("YYYY-MM-DD") : null;

      const params = {
        filters: {
          page: page,
          per_page: INCIDENT_LIST_LIMIT,
          search: search,
          sort_field: sortBy,
          sort_order: sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
        },
        bodyFilter: {
          status: statusValue,
          outcome_type: selectedOutcome?.value || null,
          participant_id: selectedParticipant?.value,
          worker_id: selectedWorker?.value,
          date_from: dateFrom,
          date_to: dateTo,
        },
      };

      getIncidentListCall(params);
      // Delay lets user see toast before clearing selection
      setTimeout(() => {
        setCheckboxes([]);
        setAlreadyGeneratedCount(0);
        resetBulkPdfCall();
      }, 2000);
    }
  }, [bulkPdfSuccess, resetBulkPdfCall]);

  // Refresh list after delete
  useEffect(() => {
    if (deleteSuccess) {
      toast.success("Incident deleted successfully!");
      resetDeleteCall();
      setPage(1);
      getIncidentListCall({
        filters: {
          page: 1,
          per_page: INCIDENT_LIST_LIMIT,
          sort_field: sortBy,
          sort_order: sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
        },
        bodyFilter: null,
      });
      getIncidentDashboardCall({});
    }
  }, [deleteSuccess]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-800",
      submitted: "bg-blue-100 text-blue-800",
      under_investigation: "bg-yellow-100 text-yellow-800",
      closed: "bg-green-100 text-green-800",
      reported: "bg-purple-100 text-purple-800",
    };
    return (
      <span
        className={classNames(
          "px-2 py-1 rounded-full text-xs font-medium",
          styles[status] || "bg-gray-100",
        )}
      >
        {INCIDENT_STATUS_LABELS[status] || status}
      </span>
    );
  };

  const getOutcomeBadge = (outcome) => {
    const styles = {
      hazard: "bg-orange-100 text-orange-800",
      near_miss: "bg-amber-100 text-amber-800",
      incident: "bg-red-100 text-red-800",
      medication: "bg-indigo-100 text-indigo-800",
    };
    const label =
      OUTCOME_OPTIONS.find((o) => o.value === outcome)?.label || outcome;
    return (
      <span
        className={classNames(
          "px-2 py-1 rounded-full text-xs font-medium",
          styles[outcome] || "bg-gray-100",
        )}
      >
        {label}
      </span>
    );
  };

  const dashboard = incidentDashboard || {};

  return (
    <div className="min-h-full">
      <SideBarNew />
      <div
        className={classNames(
          "flex flex-col",
          sideBarPadding
            ? "lg:pl-[4rem] duration-300"
            : "lg:pl-64 duration-300",
        )}
      >
        <main className="flex-1 bg-gray-50 min-h-screen">
          <Header
            title="Incident Management"
            breadcrumb={<Breadcrumb pages={BREADCRUMB} />}
          />

          <div
            className={classNames(
              "w-full px-4 sm:px-6 lg:px-8 py-6",
              checkboxes.length > 0 && "pb-24"
            )}
          >
            {/* ===== ZONE A: Operational Overview ===== */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <FiActivity className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Operational Overview
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {incidentDashboardLoading ? (
                  <div className="col-span-full flex justify-center py-8">
                    <CircularLoader width_size="w-16" />
                  </div>
                ) : (
                  <>
                    <DashboardCard
                      title="Total Incidents"
                      count={dashboard.total}
                      icon={FiFileText}
                      color="text-gray-700"
                      accentColor="#6B7280"
                      onClick={() => handleClearFilters()}
                      isActive={!selectedStatus && !fromDate}
                    />
                    <DashboardCard
                      title="This Week"
                      count={dashboard.this_week || 0}
                      icon={BiTime}
                      color="text-blue-600"
                      accentColor="#3B82F6"
                      trend={dashboard.week_trend}
                      trendValue={dashboard.week_change || "vs last week"}
                      onClick={() => {
                        // Get current week: Monday to Sunday (or start of week to today)
                        const startOfWeek = moment()
                          .startOf("isoWeek")
                          .format("YYYY-MM-DD"); // Monday
                        const today = moment().format("YYYY-MM-DD");
                        setFromDate(startOfWeek);
                        setToDate(today);
                        setSelectedStatus(null);
                        setPage(1);
                        // Pass dates directly to fetch since state updates are async
                        fetchIncidents({
                          page: 1,
                          date_from: startOfWeek,
                          date_to: today,
                        });
                      }}
                      isActive={fromDate && !selectedStatus}
                    />
                    <DashboardCard
                      title="Closed This Week"
                      count={dashboard.closed_this_week || 0}
                      icon={FiCheckCircle}
                      color="text-green-600"
                      accentColor="#22C55E"
                      onClick={() => {
                        // Get current week: Monday to Sunday (or start of week to today)
                        const startOfWeek = moment()
                          .startOf("isoWeek")
                          .format("YYYY-MM-DD"); // Monday
                        const today = moment().format("YYYY-MM-DD");
                        setFromDate(startOfWeek);
                        setToDate(today);
                        setSelectedStatus({
                          value: "closed",
                          label: INCIDENT_STATUS_LABELS.closed,
                        });
                        setPage(1);
                        // Pass both status and dates directly
                        fetchIncidents({
                          page: 1,
                          status: "closed",
                          date_from: startOfWeek,
                          date_to: today,
                        });
                      }}
                      isActive={selectedStatus?.value === "closed" && fromDate}
                    />
                    <DashboardCard
                      title="Investigation Pending"
                      count={dashboard.submitted}
                      icon={FiClock}
                      color="text-yellow-600"
                      accentColor="#EAB308"
                      onClick={() => handleDashboardCardClick("submitted")}
                      isActive={selectedStatus?.value === "submitted"}
                    />
                  </>
                )}
              </div>
            </div>

            {/* ===== ZONE B: Compliance Status ===== */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <FiShield className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Compliance Status
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {!incidentDashboardLoading && (
                  <>
                    <DashboardCard
                      title="Reportable (Pending)"
                      count={dashboard.reportable_pending || 0}
                      icon={FiAlertTriangle}
                      color="text-red-600"
                      accentColor="#EF4444"
                      onClick={() =>
                        handleDashboardCardClick("under_investigation")
                      }
                      isActive={selectedStatus?.value === "under_investigation"}
                    />
                    <DashboardCard
                      title="Reported This Week"
                      count={dashboard.reported_this_week || 0}
                      icon={HiOutlineClipboardList}
                      color="text-purple-600"
                      accentColor="#9333EA"
                      onClick={() => {
                        // Get current week: Monday to Sunday (or start of week to today)
                        const startOfWeek = moment()
                          .startOf("isoWeek")
                          .format("YYYY-MM-DD"); // Monday
                        const today = moment().format("YYYY-MM-DD");
                        setFromDate(startOfWeek);
                        setToDate(today);
                        setSelectedStatus({
                          value: "reported",
                          label: INCIDENT_STATUS_LABELS.reported,
                        });
                        setPage(1);
                        // Pass both status and dates directly
                        fetchIncidents({
                          page: 1,
                          status: "reported",
                          date_from: startOfWeek,
                          date_to: today,
                        });
                      }}
                      isActive={
                        selectedStatus?.value === "reported" && fromDate
                      }
                    />
                    <DashboardCard
                      title="Non-Reportable"
                      count={
                        dashboard.non_reportable ??
                        dashboard.total - (dashboard.reported || 0)
                      }
                      icon={AiOutlineCheckCircle}
                      color="text-teal-600"
                      accentColor="#14B8A6"
                      onClick={() => {
                        // Filter to show non-reportable incidents (closed but not reported)
                        setSelectedStatus({
                          value: "closed",
                          label: INCIDENT_STATUS_LABELS.closed,
                        });
                        setFromDate(null);
                        setToDate(null);
                        setPage(1);
                        // Fetch closed incidents (non-reportable means closed without being reported)
                        fetchIncidents({
                          page: 1,
                          status: "closed",
                        });
                      }}
                      isActive={selectedStatus?.value === "closed" && !fromDate}
                    />
                  </>
                )}
              </div>
            </div>

            {/* ===== Call to Action Banner - Drafts ===== */}
            {dashboard.draft > 0 && (
              <div
                onClick={() => handleDashboardCardClick("draft")}
                className="mb-6 bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-full">
                      <AiOutlineEdit className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-800">
                        You have{" "}
                        <span className="text-blue-600 text-xl">
                          {dashboard.draft}
                        </span>{" "}
                        draft report{dashboard.draft > 1 ? "s" : ""} to complete
                      </p>
                      <p className="text-sm text-gray-600">
                        Click to view and continue your drafts
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 group-hover:translate-x-1 transition-transform">
                    <span className="font-medium">View Drafts</span>
                    <BsArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            )}

            {/* ===== Call to Action Banner - Submitted (Only for Managers/Admins) ===== */}
            {isManagerOrAdmin && dashboard.submitted > 0 && (
              <div
                onClick={() => handleDashboardCardClick("submitted")}
                className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-100 rounded-full">
                      <AiOutlineWarning className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-amber-800">
                        You have{" "}
                        <span className="text-amber-600 text-xl">
                          {dashboard.submitted}
                        </span>{" "}
                        report{dashboard.submitted > 1 ? "s" : ""} ready for
                        review
                      </p>
                      <p className="text-sm text-amber-600">
                        Click to view pending investigation reports
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-amber-600 group-hover:translate-x-1 transition-transform">
                    <span className="font-medium">Review Now</span>
                    <BsArrowRight className="w-5 h-5" />
                  </div>
                </div>
              </div>
            )}

            {/* Actions Bar */}
            <div className="bg-white rounded-lg shadow mb-6 p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Search
                    setSearch={setSearch}
                    search={search}
                    placeholder="Search by Reported By"
                    width="w-64"
                  />
                  <CustomButton
                    variant={showFilters ? "primary" : "secondary"}
                    label="Filters"
                    Icon={<BiFilterAlt />}
                    clickHandler={() => setShowFilters(!showFilters)}
                  />
                  {/* Active Filter Indicator */}
                  {(selectedStatus ||
                    selectedOutcome ||
                    selectedParticipant ||
                    selectedWorker ||
                    fromDate) && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full">
                      <span className="text-xs font-medium text-blue-700">
                        {[
                          selectedStatus && `Status: ${selectedStatus.label}`,
                          selectedOutcome && `Type: ${selectedOutcome.label}`,
                          selectedParticipant &&
                            `${participantLabel}: ${selectedParticipant.label}`,
                          selectedWorker && `Worker: ${selectedWorker.label}`,
                          fromDate && "Date Range",
                        ]
                          .filter(Boolean)
                          .join(" • ")}
                      </span>
                      <button
                        onClick={handleClearFilters}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {roleAccess(
                  [
                    USER_ROLE.Sp_Admin,
                    USER_ROLE.Sp_manager,
                    USER_ROLE.Sp_Standard_User,
                  ],
                  PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                  PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT.CREATE,
                ) && (
                  <CustomButton
                    variant="primary"
                    label="New Incident"
                    Icon={<AiOutlinePlus />}
                    isDisabled={isUserGracePeriodOver}
                    clickHandler={() => navigate("/incident-management/create")}
                  />
                )}
              </div>

              {/* Filters Panel */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 [&>div]:min-w-0">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <SelectOption
                        data={INCIDENT_STATUS_OPTIONS}
                        selectedOption={selectedStatus}
                        handleChange={setSelectedStatus}
                        placeHolder="Select Status"
                        isClearable
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Outcome Type
                      </label>
                      <SelectOption
                        data={OUTCOME_OPTIONS}
                        selectedOption={selectedOutcome}
                        handleChange={setSelectedOutcome}
                        placeHolder="Select Type"
                        isClearable
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {participantLabel}
                      </label>
                      <SelectOption
                        data={participantOptions}
                        selectedOption={selectedParticipant}
                        handleChange={setSelectedParticipant}
                        placeHolder={`Select ${participantLabel}`}
                        isSearch
                        isClearable
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Worker
                      </label>
                      <SelectOption
                        data={workerOptions}
                        selectedOption={selectedWorker}
                        handleChange={setSelectedWorker}
                        placeHolder="Select Worker"
                        isSearch
                        isClearable
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date Range
                      </label>
                      <DateRangePickerNew
                        fromDate={fromDate}
                        toDate={toDate}
                        setSelectedFromDate={setSelectedFromDate}
                        maxDate={
                          selectedFromDate
                            ? addMonths(selectedFromDate, 3)
                            : undefined
                        }
                        handleChange={(obj) => {
                          if (obj) {
                            setFromDate(obj.fromDate);
                            setToDate(obj.toDate);
                          }
                        }}
                        handleCancel={() => {
                          setFromDate(null);
                          setToDate(null);
                        }}
                      />
                    </div>
                    <div className="md:col-span-2 flex items-end gap-2">
                      <CustomButton
                        variant="primary"
                        label="Apply Filters"
                        clickHandler={handleApplyFilters}
                      />
                      <CustomButton
                        variant="secondary"
                        label="Clear"
                        clickHandler={handleClearFilters}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Incidents Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="w-12 px-4 py-4 align-middle text-center text-sm font-semibold text-gray-900"
                      >
                        <CheckBox
                          cssClass="transition-all duration-200 ease-in-out cursor-pointer accent-[#9333EA]"
                          checked={allSelected}
                          handleChange={(e) => {
                            if (incidents && incidents.length > 0) {
                              toggleCheckboxes(e);
                            }
                          }}
                        />
                      </th>
                      <TableHeader
                        sortName="id"
                        flipSort={handleSort}
                        sortBy={sortBy}
                        sortDescending={sortDescending}
                        label="ID"
                      />
                      <TableHeader
                        isSort={false}
                        label={participantLabel}
                      />
                      <TableHeader
                        isSort={false}
                        label="Workers Involved"
                      />
                      <TableHeader
                        isSort={false}
                        label="Reported By"
                      />
                      <TableHeader
                        isSort={false}
                        label="Date"
                      />
                      <TableHeader isSort={false} label="Outcome" />
                      <TableHeader isSort={false} label="Status" />
                      <TableHeader isSort={false} label="Actions" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {incidentListLoading ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center">
                          <CircularLoader width_size="w-16" />
                        </td>
                      </tr>
                    ) : incidents.length === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No incidents found
                        </td>
                      </tr>
                    ) : (
                      incidents.map((incident) => {
                        // Same eligibility as toggleCheckboxes
                        const isEligible = incident.status === "closed" || incident.status === "reported";
                        const isChecked = checkboxes.indexOf(incident.uuid) !== -1;
                        return (
                        <tr key={incident.id} className="hover:bg-gray-50">
                          <td className="w-12 px-4 py-4 align-middle text-center whitespace-nowrap text-sm text-gray-500">
                            <div className={`text-gray-500 ${!isEligible ? "cursor-default" : ""}`} style={!isEligible ? { pointerEvents: "none" } : {}}>
                              <CheckBox
                                cssClass="transition-all duration-200 ease-in-out cursor-pointer accent-[#9333EA]"
                                handleChange={(e) => {
                                  if (isEligible) {
                                    handleCheckboxChange(e, incident.uuid);
                                  }
                                }}
                                checked={isChecked}
                                isDisabled={!isEligible}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            IN-{incident.id}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {incident.participants &&
                            incident.participants.length > 0 ? (
                              incident.participants.length === 1 ? (
                                <span className="truncate whitespace-nowrap overflow-ellipsis block text-sm text-gray-900">
                                  {capitalizeWords(
                                    incident.participants[0]?.name,
                                  ) || "N/A"}
                                </span>
                              ) : (
                                <AvatarGroup
                                  arrayData={incident.participants.map((p) => ({
                                    ...p,
                                    first_name: p.first_name || "",
                                    last_name: p.last_name || "",
                                    profile_picture: p.profile_picture,
                                  }))}
                                  cssClass="mt-0"
                                  maxLength={3}
                                  isNoRing={true}
                                />
                              )
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {incident.workers &&
                            incident.workers.length > 0 ? (
                              incident.workers.length === 1 ? (
                                <span className="truncate whitespace-nowrap overflow-ellipsis block text-sm text-gray-900">
                                  {capitalizeWords(
                                    incident.workers[0]?.name,
                                  ) || "N/A"}
                                </span>
                              ) : (
                                <AvatarGroup
                                  arrayData={incident.workers.map((w) => ({
                                    ...w,
                                    first_name: w.first_name || "",
                                    last_name: w.last_name || "",
                                    profile_picture: w.profile_picture,
                                  }))}
                                  cssClass="mt-0"
                                  maxLength={3}
                                  isNoRing={true}
                                />
                              )
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {capitalizeWords(incident.created_by_name) || "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {incident.incident_date
                              ? moment(incident.incident_date).format(
                                  "DD/MM/YYYY",
                                )
                              : "-"}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getOutcomeBadge(incident.outcome_type)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getStatusBadge(incident.status)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-center gap-x-2">
                              {/* PDF Download Button - Only for closed or reported status */}
                              {(incident.status === "closed" ||
                                incident.status === "reported") && (
                                <button
                                  onClick={() => handlePdfDownload(incident)}
                                  disabled={pdfLoadingUuid === incident.uuid || !isPdfGenerated(incident)}
                                  title={
                                    pdfLoadingUuid === incident.uuid
                                      ? "Generating PDF..."
                                      : isPdfGenerated(incident)
                                      ? "Download PDF"
                                      : "PDF not yet generated"
                                  }
                                >
                                  <div
                                    className={`icon-filePdf-wrapper ${
                                      pdfLoadingUuid === incident.uuid || !isPdfGenerated(incident)
                                        ? "opacity-30 cursor-not-allowed"
                                        : "cursor-pointer"
                                    }`}
                                  >
                                    <AiOutlineFilePdf className="icon-size" />
                                  </div>
                                </button>
                              )}

                              {/* View Button - Check view permission */}
                              {roleAccess(
                                [
                                  USER_ROLE.Sp_Admin,
                                  USER_ROLE.Sp_manager,
                                  USER_ROLE.Sp_Standard_User,
                                ],
                                PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                                PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT
                                  .VIEW,
                              ) && (
                                <button
                                  onClick={() =>
                                    navigate(
                                      `/incident-management/view/${incident.uuid}`,
                                    )
                                  }
                                  title="View"
                                >
                                  <div className="icon-eye-wrapper">
                                    <HiEye className="icon-size" />
                                  </div>
                                </button>
                              )}

                              {/* Resume/Edit Button */}
                              {(() => {
                                // Check edit permission first
                                const hasEditPermission = roleAccess(
                                  [
                                    USER_ROLE.Sp_Admin,
                                    USER_ROLE.Sp_manager,
                                    USER_ROLE.Sp_Standard_User,
                                  ],
                                  PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                                  PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT
                                    .EDIT,
                                );
                                if (!hasEditPermission) return null;

                                const isManager = roleAccess([
                                  USER_ROLE.Sp_Admin,
                                  USER_ROLE.Sp_manager,
                                ]);
                                const isDraft = incident.status === "draft";
                                const isSubmitted = incident.status === "submitted";
                                const canManagerEdit =
                                  isManager &&
                                  ["under_investigation", "reported", "closed"].includes(
                                    incident.status,
                                  );

                                let node = null;

                                // Draft - Show Resume icon (play button)
                                if (isDraft) {
                                  node = (
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/incident-management/edit/${incident.uuid}`,
                                        )
                                      }
                                      title="Resume"
                                    >
                                      <div className="icon-stopPlay-wrapper">
                                        <MdOutlineNotStarted className="icon-size" />
                                      </div>
                                    </button>
                                  );
                                }

                                // Submitted or Manager can edit - Show Edit icon (pencil)
                                if (!node && (isSubmitted || canManagerEdit)) {
                                  node = (
                                    <button
                                      onClick={() =>
                                        navigate(
                                          `/incident-management/edit/${incident.uuid}`,
                                        )
                                      }
                                      title="Edit"
                                    >
                                      <div className="icon-pencil-wrapper">
                                        <BiPencil className="icon-size" />
                                      </div>
                                    </button>
                                  );
                                }

                                // Don't render an empty slot (prevents visual gap)
                                return node ? (
                                  <div className="w-7 h-7 flex items-center justify-center">
                                    {node}
                                  </div>
                                ) : null;
                              })()}

                              {/* Delete Button */}
                              {(() => {
                                // Check delete permission first
                                const hasDeletePermission = roleAccess(
                                  [USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager],
                                  PERMISSION_KEYS.INCIDENT_MANAGEMENT,
                                  PERMISSION_KEYS_CHILDREN.INCIDENT_MANAGEMENT
                                    .DELETE,
                                );
                                if (!hasDeletePermission) return null;

                                const isManager = roleAccess([
                                  USER_ROLE.Sp_Admin,
                                  USER_ROLE.Sp_manager,
                                ]);
                                const canDelete =
                                  incident.status === "draft" || isManager;

                                if (!canDelete) return null;

                                return (
                                  <div className="w-7 h-7 flex items-center justify-center">
                                    <button
                                      onClick={() => handleDeleteClick(incident)}
                                      title="Delete"
                                    >
                                      <div className="icon-delete-wrapper">
                                        <HiTrash className="icon-size" />
                                      </div>
                                    </button>
                                  </div>
                                );
                              })(                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!incidentListLoading && incidents.length > 0 && paginationObj.totalPages > 0 && (
                <div className="px-4 py-3 border-t border-gray-200">
                  {/* Use page state (not paginationObj.page) to avoid sync issues */}
                  <PaginationComponent
                    page={page}
                    activeListLength={incidents}
                    limit={INCIDENT_LIST_LIMIT}
                    totalList={paginationObj.total || 0}
                    totalPages={paginationObj.totalPages || 1}
                    handlePagination={(newPage) => {
                      // Prevent pagination clicks during loading
                      if (incidentListLoading) {
                        return;
                      }
                      setPage(newPage)

                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <SweetAlertConfirmationModal
          isOpen={showDeleteModal}
          title="Delete Incident Report?"
          content={`Are you sure you want to delete incident IN-${selectedIncident?.id}? This action cannot be undone.`}
          setIsConfirm={handleDeleteConfirm}
          dangerMode={true}
          onlyOkBtn={["No, Cancel", "Yes, Delete"]}
        />
      )}

      {/* Floating Action Bar */}
      {checkboxes.length > 0 && (
        <div
          className="fixed bottom-0 bg-white shadow-lg z-50 border-t border-gray-200"
          style={{
            left: sideBarPadding ? '4rem' : '16rem',
            right: '0',
            marginBottom: '0'
          }}
        >
          <div className="w-full px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Left side - Selection info */}
              <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
                <span className="text-base font-semibold text-gray-900 whitespace-nowrap">
                  {checkboxes.length} Report{checkboxes.length !== 1 ? "s" : ""} Selected
                </span>
                {(() => {
                  const currentPageUuids = (incidentList?.incidents?.data || []).map((i) => i.uuid);
                  const hasSelectionsOffPage = checkboxes.some((uuid) => !currentPageUuids.includes(uuid));
                  return hasSelectionsOffPage ? (
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      across multiple pages
                    </span>
                  ) : null;
                })()}
                {alreadyGeneratedCount > 0 && (
                  <span className="text-sm text-gray-600 hidden lg:inline whitespace-nowrap">
                    ({alreadyGeneratedCount} PDF(s) already generated)
                  </span>
                )}
              </div>

              {/* Right side - Actions */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={() => {
                    setCheckboxes([]);
                    setAlreadyGeneratedCount(0);
                  }}
                  className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-all px-4 py-2 rounded-lg hover:bg-gray-100 whitespace-nowrap"
                  disabled={bulkPdfLoading}
                >
                  Clear Selection
                </button>
                <CustomButton
                  label="Generate PDF"
                  clickHandler={handleBulkGeneratePdf}
                  variant="primary"
                  showLoading={bulkPdfLoading}
                  isDisabled={bulkPdfLoading || checkboxes.length === 0}
                  cssClass="min-w-[130px] font-semibold shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  incidentList: getIncidentList(state),
  incidentListLoading: getIncidentListLoading(state),
  incidentDashboard: getIncidentDashboard(state),
  incidentDashboardLoading: getIncidentDashboardLoading(state),
  workersList: getSpWorkers(state),
  deleteLoading: getIncidentDeleteLoading(state),
  deleteSuccess: getIncidentDeleteSuccess(state),
  assignedParticipants: getAssignedParticipants(state),
  settings: getSpSetting(state),
  bulkPdfLoading: getBulkPdfGenerateLoading(state),
  bulkPdfSuccess: getBulkPdfGenerateSuccess(state),
});

const mapDispatchToProps = {
  getIncidentListCall: incidentActions.getIncidentListRequest,
  getIncidentDashboardCall: incidentActions.getIncidentDashboardRequest,
  getWorkerListCall: getSPWorkerRequest,
  getAssignedParticipantsCall: incidentActions.getAssignedParticipantsRequest,
  deleteIncidentCall: incidentActions.deleteIncidentRequest,
  resetDeleteCall: incidentActions.resetIncidentDelete,
  bulkGeneratePdfCall: incidentActions.bulkGeneratePdfRequest,
  resetBulkPdfCall: incidentActions.resetBulkPdfGenerate,
};

export default connect(mapStateToProps, mapDispatchToProps)(IncidentDashboard);
